import Vue from 'vue'
import Vuex from 'vuex'
import axios from "axios";
import notifier from './notifier.js'
import plugin from './plugin.js'

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {
    app_loading: false,
    accessToken: localStorage.getItem('token') || '',
    auth_user: null,
    auth_user_profile_type: localStorage.getItem('profile_type') || '',
    auth_user_course_leader: false,
    auth_user_admin: false,
    auth_user_timezone: null,
    auth_user_notifications: [],
    auth_user_study_groups: [],
    auth_user_session_limits: [],

    countries: [],
    session_types: [],
    practice_areas: [],
    businessTypes: [],
    majorPositions: [],
    educationLevels: [],
    qualifications: [],
  },
  mutations: {
    SET_APP_LOADING(state, payload) {
      state.app_loading = payload
    },
    SET_AUTH_TOKEN(state, payload) {
      state.accessToken = payload
      localStorage.setItem('token', payload)
    },
    SET_AUTH_USER(state, payload) {
      state.auth_user = payload
      state.auth_user_timezone = payload.timezone
      state.auth_user_profile_type = payload.profile_type || null
      localStorage.setItem('profile_type', payload.profile_type || null)
      state.auth_user_course_leader = payload.course_leader || false
      state.auth_user_admin = payload.admin || false
    },
    SET_AUTH_USER_STUDY_GROUPS(state, payload) {
      state.auth_user_study_groups = payload
    },
    SET_AUTH_USER_SESSION_LIMITS(state, payload) {
      state.auth_user_session_limits = payload
    },

    SET_SESSION_TYPES(state, payload) {
      state.session_types = payload
    },
    SET_PRACTICE_AREAS(state, payload) {
      state.practice_areas = payload
    },

    SET_COUNTRIES (state, countries) {
      state.countries = countries
    },
    SET_BUSINESS_TYPES (state, businessTypes) {
      state.businessTypes = businessTypes
    },
    SET_MAJOR_POSITIONS (state, majorPositions) {
      state.majorPositions = majorPositions
    },
    SET_EDUCATION_LEVELS (state, levels) {
      state.educationLevels = levels
    },
    SET_QUALIFICATIONS (state, qualifications) {
      state.qualifications = qualifications
    },

    CLEAR_STORAGE(state) {
      state.accessToken = null
      localStorage.removeItem('token')
      state.auth_user = null
      state.auth_user_timezone = null
      state.auth_user_profile_type = null
      localStorage.removeItem('profile_type')
      state.auth_user_course_leader = false
      state.auth_user_admin = false
      state.auth_user_study_groups = []
      state.auth_user_session_types = []
    }
  },
  actions: {
    loadUser ({ commit }) {
      return new Promise((resolve, reject) => {
        axios.post('api/user')
            .then(res => {
              commit('SET_AUTH_USER', res.data.data)
              resolve(res)
            })
            .catch(errors => { reject(errors.response.data) })
      })
    },
    loadAuthUserStudyGroups({ commit }){
      return new Promise((resolve, reject) => {
        axios.get('api/user/study-groups')
            .then((response) => {
              commit('SET_AUTH_USER_STUDY_GROUPS', response.data.data)
              resolve(response)
            })
            .catch(errors => { reject(errors) })
      })
    },
    loadAuthUserSessionLimits({ commit }){
      return new Promise((resolve, reject) => {
        axios.get('api/user/session-limits')
            .then((response) => {
              commit('SET_AUTH_USER_SESSION_LIMITS', response.data.data)
              resolve(response)
            })
            .catch(errors => { reject(errors) })
      })
    },

    loadSessionTypes({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/session-types')
            .then(({data}) => {
              commit('SET_SESSION_TYPES', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))
      })
    },
    loadPracticeAreas({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/practice-areas')
            .then(({data}) => {
              commit('SET_PRACTICE_AREAS', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))
      })
    },
    async loadCountries({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/countries')
            .then(({data}) => {
              commit('SET_COUNTRIES', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))
      })
    },
    async loadEducationLevels({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/education-levels')
            .then(({data}) => {
              commit('SET_EDUCATION_LEVELS', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))
      })
    },
    async loadBusinessTypes({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/business-types')
            .then(({data}) => {
              commit('SET_BUSINESS_TYPES', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))
      })
    },
    async loadMajorPositions({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/major-positions')
            .then(({data}) => {
              commit('SET_MAJOR_POSITIONS', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))
      })
    },
    async loadQualifications({commit}) {
      return new Promise((resolve, reject) => {
        axios.get('api/qualifications')
            .then(({data}) => {
              commit('SET_QUALIFICATIONS', data.data)
              resolve(data.data)
            })
            .catch(errors => reject(errors))

      })
    },
  },
  getters: {
    getAppLoading: state => state.app_loading,
    isLoggedIn: state => !!state.accessToken,
    getAuthToken: state => state.accessToken,
    getAuthUser: state => state.auth_user,
    getAuthUserTimezone: state => state.auth_user_timezone,
    getAuthUserProfileType: state => state.auth_user_profile_type,
    getAuthUserProfileTypeLabel: state => {
      switch (state.auth_user_profile_type) {
        case 'tutoringSolicitor':
          return 'Tutoring Solicitor'
        case 'interviewer':
          return 'Interviewer'
        default:
          return 'Aspiring Solicitor'
      }
    },
    authUserCourseLeader: state => state.auth_user_course_leader,
    authUserAdmin: state => state.auth_user_admin,
    getAuthUserStudyGroups: state => state.auth_user_study_groups,
    getAuthUserSessionLimits: state => state.auth_user_session_limits,

    getSessionTypes: state => state.session_types,
    getPracticeAreas: state => state.practice_areas,
    getCountries: state => state.countries,
    getBusinessTypes: state => state.businessTypes,
    getEducationLevels: state => state.educationLevels,
    getMajorPositions: state => state.majorPositions,
    getQualifications: state => state.qualifications,
  },
  modules: {
    notifier: notifier,
  }
})

Vue.use(plugin, store)

export default store

